/**
 * Button Component - RABBIT Design System
 *
 * CTA options: variant × size, optional TXT + ICON via leftIcon/rightIcon
 * (icon inherits label size and color — see the "Text + icon" section of the
 * /design showcase).
 *
 * Supports three render modes:
 * - default: <button>
 * - href: <a> for external links
 * - to: <Link> from react-router-dom for internal navigation
 */

import React from 'react';
import { Link } from 'react-router-dom';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  /** Bouton carré à icône seule (largeur = hauteur, coins --radius-md) —
      ex. bouton Run de la topbar du GeneratePanel. */
  square?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children: React.ReactNode;
}

type NativeButtonProps = ButtonBaseProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps>;

type AnchorButtonProps = ButtonBaseProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps> & {
    href: string;
  };

type LinkButtonProps = ButtonBaseProps &
  Omit<React.ComponentPropsWithoutRef<typeof Link>, keyof ButtonBaseProps> & {
    to: string;
  };

export type ButtonProps = NativeButtonProps | AnchorButtonProps | LinkButtonProps;

export const Button = React.forwardRef<HTMLElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      square = false,
      loading = false,
      leftIcon,
      rightIcon,
      children,
      className = '',
      ...props
    },
    ref
  ) => {
    const classes = [
      'ds-button',
      `ds-button--${variant}`,
      `ds-button--${size}`,
      fullWidth && 'ds-button--full-width',
      square && 'ds-button--square',
      loading && 'ds-button--loading',
      className,
    ].filter(Boolean).join(' ');

    const content = (
      <>
        {loading && <span className="ds-button__spinner" />}
        {leftIcon && !loading && (
          <span className="ds-button__icon ds-button__icon--left">{leftIcon}</span>
        )}
        <span className="ds-button__label">{children}</span>
        {rightIcon && !loading && (
          <span className="ds-button__icon ds-button__icon--right">{rightIcon}</span>
        )}
      </>
    );

    if ('to' in props && props.to) {
      const { to, ...linkProps } = props as LinkButtonProps;
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={to}
          className={classes}
          {...linkProps}
        >
          {content}
        </Link>
      );
    }

    if ('href' in props && props.href) {
      const { href, ...anchorProps } = props as AnchorButtonProps;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={classes}
          {...anchorProps}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={classes}
        disabled={loading || (props as NativeButtonProps).disabled}
        {...(props as NativeButtonProps)}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
